import React, { useContext, useEffect } from "react";
import { createContext, ReactNode, useState } from "react";
import {
  CUP_DATA_DEFAULT,
  SHIPMENT_DATA_DEFAULT,
  TOTAL_UNITS_ACCESSOR,
} from "../pages/components/ShipmentMonthTables/ShipmentMonthTables.constants";
import useCellTab from "../hooks/useCellTab";
import { ForecastContext } from "./ForecastContext";
import { calculateQuarterlyValues } from "../constants/utils";
import { getLongTermSeason } from "../pages/components/SeasonToMonth/header.utils";

const CUP_ACCESSOR = 0;
export const SeasonToMonthContext = createContext<{
  seasonToMonthData: number[][];
  setSeasonToMonthData: (val: number[][]) => void;
  cupData: number[][];
  setCupData: (val: number[][]) => void;
  selectedCell: { row: number; column: number };
  setNextCell: (row: number, column: number) => () => void;
}>({
  seasonToMonthData: [[1]],
  setSeasonToMonthData: (val) => val,
  cupData: [[1]],
  setCupData: (val) => val,
  selectedCell: { row: -1, column: -1 },
  setNextCell: (row, column) => () => row + column,
});

export const SeasonToMonthProvider = ({
  children,
  date,
}: {
  children: ReactNode;
  date: Date;
}) => {
  const { originalForecast } = useContext(ForecastContext);
  const [seasonToMonthData, setSeasonToMonthData] = useState<number[][]>(
    SHIPMENT_DATA_DEFAULT,
  );
  const [cupData, setCupData] = useState<number[][]>(CUP_DATA_DEFAULT);
  // Always 4 long term seasons (4*3) by 4 seasons
  const { selectedCell, setNextCell } = useCellTab(12, 4);
  useEffect(() => {
    if (originalForecast.history && originalForecast.history.length === 4) {
      const newSeasonToMonthData = originalForecast.history.map((forecast) => {
        return forecast.map((percent) => percent);
      });
      const cupData = originalForecast.history_pg2n_totals.map(
        (forecast) => forecast,
      );
      transformData(newSeasonToMonthData, cupData);
    }
  }, [originalForecast]);
  const LONG_TERM_SEASON = getLongTermSeason(date);
  const LONG_TERM_START_MONTH = LONG_TERM_SEASON * 3;
  const transformData = (
    newSeasonToMonthData: number[][],
    incomingCupData: number[][],
  ) => {
    const mappedData = newSeasonToMonthData.map((shipmentHistory) => {
      const histories = shipmentHistory.map((history) => history);
      const total = histories.reduce((acc, curr) => acc + curr, 0);
      const quarterlyValues = calculateQuarterlyValues(
        histories,
        LONG_TERM_START_MONTH,
      );
      return histories.concat(total).concat(quarterlyValues);
    });
    const newCupData = mappedData.map((row, i) => {
      const newRow = row
        .slice(0, TOTAL_UNITS_ACCESSOR)
        .map((value) => (value / 100) * incomingCupData[i][CUP_ACCESSOR]);
      const total = newRow.reduce((acc, val) => acc + val, 0);
      const variance = total - incomingCupData[i][0];
      return newRow
        .concat(total)
        .concat(incomingCupData[i][CUP_ACCESSOR])
        .concat(variance);
    });
    const LRTotals = newCupData.reduce((acc, value) => {
      if (!acc.length) {
        return value.map((val) => Math.round(val));
      }
      return acc.map((val, i) => val + Math.round(value[i]));
    }, []);
    setCupData(newCupData.concat([LRTotals]));
    setSeasonToMonthData(mappedData);
  };
  return (
    <SeasonToMonthContext.Provider
      value={{
        seasonToMonthData,
        setSeasonToMonthData,
        cupData,
        setCupData,
        selectedCell,
        setNextCell,
      }}
    >
      {children}
    </SeasonToMonthContext.Provider>
  );
};
